import { useState } from 'react';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { Card } from 'components/Card';
import { DataField } from 'components/DataField';
import { Button } from 'components/button/Button';
import { Tooltip } from 'components/tooltip/Tooltip';
import { Alert } from 'components/alert/Alert';
import { formatDateTime } from '@utils/date';
import { getDisplayError } from '@utils/get-display-error';

import { PEPPOL_ALREADY_SENT_STATUSES, type PeppolDelivery, type SendPeppolResponse } from '../peppol.types';
import { describePeppolAlreadySent, PEPPOL_STATUS_LABELS } from '../peppol-status-labels';
import { PeppolStatusPill } from './PeppolStatusPill';

interface IPeppolSendButtonProps {
  peppol: PeppolDelivery;
  blockReason: string | null;
  isSending: boolean;
  onSend: () => Promise<void>;
}

/** Disabled with the reason when blocked or when the document already went out via Peppol. */
const PeppolSendButton: React.FC<IPeppolSendButtonProps> = ({ peppol, blockReason, isSending, onSend }) => {
  const disabledReason = PEPPOL_ALREADY_SENT_STATUSES.includes(peppol.status)
    ? describePeppolAlreadySent(peppol)
    : blockReason;
  if (disabledReason) {
    return (
      <Tooltip content={disabledReason}>
        <Button color="primary" size={8} isDisabled>
          Verstuur via Peppol
        </Button>
      </Tooltip>
    );
  }
  return (
    <Button color="primary" size={8} isLoading={isSending} onTrigger={onSend}>
      Verstuur via Peppol
    </Button>
  );
};

export interface IPeppolStatusCardProps {
  peppol: PeppolDelivery;
  /** Null when the document can go out via Peppol; otherwise why the send button is disabled. */
  blockReason: string | null;
  onSend: () => Promise<SendPeppolResponse>;
  onRefresh: () => Promise<PeppolDelivery>;
  onChanged: () => unknown;
}

/** Billit/Peppol delivery status of an invoice, credit note or self-bill, with manual send and refresh. */
export const PeppolStatusCard: React.FC<IPeppolStatusCardProps> = (props) => {
  const { peppol, blockReason, onSend, onRefresh, onChanged } = props;
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isProblem = peppol.status === 'Failed' || peppol.status === 'Refused';

  const send = async () => {
    setIsSending(true);
    try {
      const result = await onSend();
      if (result.outcome === 'sent') {
        toast.success('Verstuurd via Peppol');
      } else if (result.outcome === 'alreadySent') {
        toast.error(describePeppolAlreadySent(result.peppol));
      } else {
        toast.error(`Versturen via Peppol mislukt: ${result.peppol.detail ?? 'onbekende fout'}`);
      }
    } catch (err) {
      captureException(err);
      toast.error(`Kon niet versturen via Peppol: ${getDisplayError(err)}`);
    } finally {
      setIsSending(false);
      await onChanged();
    }
  };

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await onRefresh();
      toast.success(`Peppol status: ${PEPPOL_STATUS_LABELS[result.status]}`);
    } catch (err) {
      captureException(err);
      toast.error(getDisplayError(err));
    } finally {
      setIsRefreshing(false);
      await onChanged();
    }
  };

  return (
    <Card
      title="Peppol / Billit"
      rightHeading={
        <>
          {peppol.billitOrderId && (
            <Button size={8} color="ghost" isLoading={isRefreshing} onTrigger={refresh}>
              Status vernieuwen
            </Button>
          )}
          <PeppolSendButton peppol={peppol} blockReason={blockReason} isSending={isSending} onSend={send} />
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <DataField title="Status">
            <PeppolStatusPill status={peppol.status} />
          </DataField>
          <DataField title="Verzonden op">{peppol.sentAt ? formatDateTime(peppol.sentAt) : '-'}</DataField>
          <DataField title="Laatst gecontroleerd">
            {peppol.checkedAt ? formatDateTime(peppol.checkedAt) : '-'}
          </DataField>
          <DataField title="Billit ID">{peppol.billitOrderId ?? '-'}</DataField>
        </div>
        {peppol.detail &&
          (isProblem ? (
            <Alert type="error">{peppol.detail}</Alert>
          ) : (
            <div className="text-sm text-fg-secondary">{peppol.detail}</div>
          ))}
        {blockReason && peppol.status === 'NotSent' && <div className="text-sm text-fg-muted">{blockReason}</div>}
      </div>
    </Card>
  );
};
